import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

// Poll status endpoint for up to ~30s while the webhook lands.
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", tries: 0, plan_key: null });

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", tries: 0 }); return; }
    let cancelled = false;
    const poll = async (tries = 0) => {
      if (cancelled || tries > 15) {
        setState((s) => ({ ...s, phase: tries > 15 ? "timeout" : s.phase }));
        return;
      }
      try {
        const { data } = await api.get(`/pricing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", tries, plan_key: data.plan_key });
          return;
        }
        setTimeout(() => poll(tries + 1), 2000);
      } catch (err) {
        setState({ phase: "error", tries, error: formatApiError(err) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[color:var(--canvas)]">
      <Nav/>
      <main className="max-w-2xl mx-auto px-4 py-24 text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[color:var(--primary)] animate-spin mb-6"/>
            <h1 className="text-3xl font-serif text-[color:var(--text)] mb-3">Finalising your subscription…</h1>
            <p className="text-sm text-[color:var(--text-muted)]">Hold tight while Stripe confirms the payment. This usually takes a few seconds.</p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-6" data-testid="pay-success-icon"/>
            <h1 className="text-3xl font-serif text-[color:var(--text)] mb-3">Welcome to BookForge360 {state.plan_key}!</h1>
            <p className="text-sm text-[color:var(--text-muted)] mb-8">
              Your credits are ready. Head to your library and start formatting.
            </p>
            <Link to="/dashboard" className="inline-block px-6 py-3 bg-[color:var(--text)] text-white text-sm hover:bg-[color:var(--primary)]" data-testid="pay-success-dashboard-cta">
              Go to Library
            </Link>
          </>
        )}
        {(state.phase === "error" || state.phase === "timeout") && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-6"/>
            <h1 className="text-3xl font-serif text-[color:var(--text)] mb-3">Almost there…</h1>
            <p className="text-sm text-[color:var(--text-muted)] mb-8">
              We couldn't confirm the payment yet. If Stripe already charged your card, refresh this page in a minute — otherwise reach out to support@bookforge360.com.
            </p>
            <Link to="/pricing" className="text-sm text-[color:var(--primary)] underline">Back to pricing</Link>
          </>
        )}
      </main>
    </div>
  );
}
